// ============================================================================
// AI ANALYZER - PRO Feature
// Advanced AI-powered resume analysis and enhancement
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Sparkles, FileText, Upload, Loader, AlertCircle, Lock, CheckCircle, Zap } from 'lucide-react';
import { parseResume } from '../utils/resumeParser';

const AIAnalyzer = ({ isMobile, onOpenAuth, onNavigate }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [file, setFile] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [enhancement, setEnhancement] = useState(null);
  const [error, setError] = useState('');
  const [showZeroCreditsModal, setShowZeroCreditsModal] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const userInfo = localStorage.getItem('user_info');

    if (token && userInfo) {
      setUser(JSON.parse(userInfo));
      fetchSubscription(token);
    }

    // Check for pending AI analysis from ATS Checker
    const pendingAnalysis = localStorage.getItem('pending_ai_analysis');
    if (pendingAnalysis) {
      try {
        const data = JSON.parse(pendingAnalysis);
        setResumeText(data.resumeText);
        setJobDescription(data.jobDescription || '');
        // Clear the pending analysis
        localStorage.removeItem('pending_ai_analysis');
      } catch (e) {
        console.error('Failed to load pending analysis:', e);
      }
    }
  }, []);

  const fetchSubscription = async (token) => {
    try {
      const response = await fetch('https://theresumex.cv/api/user/subscription', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.subscription) {
        setSubscription(data.subscription);
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    }
  };

  const handleFileUpload = async (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    setError('');
    setLoading(true);

    try {
      const text = await parseResume({ file: uploadedFile });
      setResumeText(text);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    // Check if user is logged in
    if (!user) {
      if (onNavigate) {
        onNavigate('pricing');
      } else {
        window.location.href = '/pricing?message=' + encodeURIComponent('Please sign in to access AI Analyzer');
      }
      return;
    }

    // Check subscription and credits
    if (!subscription || subscription.remainingAnalyzes <= 0) {
      setShowZeroCreditsModal(true);
      return;
    }

    if (!resumeText) {
      setError('Please upload a resume first');
      return;
    }

    setAnalyzing(true);
    setError('');

    try {
      const token = localStorage.getItem('auth_token');

      // Call GPT-4o API
      const response = await fetch('https://theresumex.cv/api/ai/enhance-resume', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          resumeText,
          jobDescription: jobDescription || undefined,
          customPrompt: customPrompt || undefined,
          enhancementType: 'all'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.code === 'NO_CREDITS') {
          setShowZeroCreditsModal(true);
          return;
        }
        throw new Error(data.error || 'Analysis failed');
      }

      setEnhancement(data.enhancement);

      // Refresh subscription to show updated credits
      await fetchSubscription(token);

      // Check if credits are now zero and show modal
      if (data.remainingAnalyzes === 0) {
        setTimeout(() => {
          setShowZeroCreditsModal(true);
        }, 2000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  // If not logged in, show lock screen
  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        padding: isMobile ? '40px 20px' : '80px 40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: isMobile ? '40px 24px' : '60px',
          maxWidth: '600px',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            position: 'relative'
          }}>
            <Sparkles size={40} color="white" />
            <div style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              background: '#FCD34D',
              borderRadius: '50%',
              padding: '8px',
              border: '3px solid white'
            }}>
              <Lock size={16} color="#92400E" />
            </div>
          </div>

          <h1 style={{
            fontSize: isMobile ? '28px' : '36px',
            fontWeight: '800',
            color: '#145A64',
            marginBottom: '16px'
          }}>
            AI Analyzer
          </h1>

          <p style={{
            fontSize: isMobile ? '16px' : '18px',
            color: '#666',
            lineHeight: '1.6',
            marginBottom: '32px'
          }}>
            Get comprehensive AI-powered resume analysis and improvements powered by GPT-4o.
          </p>

          <div style={{
            background: '#F9FAFB',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '32px',
            textAlign: 'left'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#145A64',
              marginBottom: '16px'
            }}>
              PRO Features Include:
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                'Complete resume rewrite suggestions',
                'Job description matching analysis',
                'Keyword optimization recommendations',
                'Section-by-section improvements',
                'Before/after comparisons',
                'ATS compatibility enhancements'
              ].map((feature, idx) => (
                <li key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px',
                  fontSize: '14px',
                  color: '#4B5563'
                }}>
                  <CheckCircle size={18} color="#10B981" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <button
            onClick={() => onNavigate ? onNavigate('pricing') : (window.location.href = '/pricing')}
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <Zap size={20} />
            {!user ? 'Sign In to Get Started' : 'Upgrade to PRO'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      padding: isMobile ? '40px 20px' : '80px 40px'
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <Sparkles size={32} color="#8B5CF6" />
            <h1 style={{
              fontSize: isMobile ? '32px' : '48px',
              fontWeight: '800',
              color: '#145A64',
              margin: 0
            }}>
              AI Analyzer
            </h1>
            <span style={{
              background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
              color: 'white',
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '700'
            }}>
              PRO
            </span>
          </div>

          <p style={{
            fontSize: isMobile ? '16px' : '18px',
            color: '#666',
            maxWidth: '700px',
            margin: '0 auto 16px'
          }}>
            Get comprehensive AI-powered analysis and improvements for your resume
          </p>

          {subscription && (
            <div style={{
              display: 'inline-block',
              padding: '10px 20px',
              background: '#10B981',
              color: 'white',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              ✨ {subscription.remainingAnalyzes} AI analyzes remaining
            </div>
          )}
        </div>

        {/* Upload Section */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: isMobile ? '24px' : '32px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#145A64',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FileText size={20} />
            Upload Your Resume
          </h2>

          <label style={{
            display: 'block',
            width: '100%',
            padding: '40px 20px',
            border: '2px dashed rgba(139, 92, 246, 0.3)',
            borderRadius: '12px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            background: 'rgba(139, 92, 246, 0.02)'
          }}>
            <Upload size={32} color="#8B5CF6" />
            <div style={{
              marginTop: '12px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#145A64'
            }}>
              {file ? file.name : 'Click to upload or drag & drop'}
            </div>
            <div style={{ marginTop: '4px', fontSize: '13px', color: '#999' }}>
              PDF or DOCX format
            </div>
            <input
              type="file"
              accept=".pdf,.docx"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>

          {loading && (
            <div style={{
              marginTop: '16px',
              textAlign: 'center',
              color: '#8B5CF6',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Parsing resume...
            </div>
          )}
        </div>

        {/* Job Description (Optional) */}
        {resumeText && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '24px' : '32px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            marginBottom: '24px'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '8px'
            }}>
              Job Description (Optional)
            </h2>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              Add a job description to get tailored recommendations
            </p>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the job description here..."
              style={{
                width: '100%',
                minHeight: '150px',
                padding: '16px',
                border: '2px solid #E5E7EB',
                borderRadius: '12px',
                fontSize: '15px',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#8B5CF6'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>
        )}

        {/* Custom Prompt (Optional) */}
        {resumeText && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '24px' : '32px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            marginBottom: '24px'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#145A64',
              marginBottom: '8px'
            }}>
              Custom Instructions (Optional)
            </h2>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
              Add specific instructions for the AI to focus on particular aspects
            </p>
            <textarea
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="E.g., 'Focus on leadership skills', 'Emphasize technical achievements', 'Make it more concise'..."
              style={{
                width: '100%',
                minHeight: '120px',
                padding: '16px',
                border: '2px solid #E5E7EB',
                borderRadius: '12px',
                fontSize: '15px',
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#8B5CF6'}
              onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
            />
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div style={{
            background: '#FEE2E2',
            border: '2px solid #FCA5A5',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: '#991B1B'
          }}>
            <AlertCircle size={20} />
            <span style={{ fontSize: '15px', fontWeight: '500' }}>{error}</span>
          </div>
        )}

        {/* Analyze Button */}
        {resumeText && (
          <button
            onClick={handleAnalyze}
            disabled={analyzing}
            style={{
              width: '100%',
              padding: '18px',
              background: analyzing ? '#ccc' : 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: '600',
              cursor: analyzing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              marginBottom: '32px',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!analyzing) e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            {analyzing ? (
              <>
                <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
                Analyzing with GPT-4o...
              </>
            ) : (
              <>
                <Sparkles size={24} />
                Analyze Resume with AI
              </>
            )}
          </button>
        )}

        {/* Enhancement Results */}
        {enhancement && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '24px' : '32px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            border: '2px solid #8B5CF6'
          }}>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#8B5CF6',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <Sparkles size={28} />
              AI-Powered Improvements
            </h3>
            <div style={{
              background: '#F9FAFB',
              padding: '24px',
              borderRadius: '12px',
              fontSize: '15px',
              lineHeight: '1.8',
              color: '#374151',
              whiteSpace: 'pre-wrap'
            }}>
              {enhancement}
            </div>
          </div>
        )}
      </div>

      {/* Zero Credits Modal */}
      {showZeroCreditsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }} onClick={() => setShowZeroCreditsModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            maxWidth: '500px',
            width: '100%',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
              fontSize: '40px'
            }}>
              ⚠️
            </div>

            <h2 style={{
              fontSize: '28px',
              fontWeight: '800',
              color: '#145A64',
              marginBottom: '16px'
            }}>
              Out of AI Credits!
            </h2>

            <p style={{
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.6',
              marginBottom: '32px'
            }}>
              You've used all your AI analyzes. Purchase more credits to continue improving your resume with AI-powered insights.
            </p>

            <button
              onClick={() => {
                setShowZeroCreditsModal(false);
                if (onNavigate) {
                  onNavigate('pricing');
                } else {
                  window.location.href = '/pricing';
                }
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #145A64 0%, #1A8A99 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                marginBottom: '12px',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Purchase More Credits
            </button>

            <button
              onClick={() => setShowZeroCreditsModal(false)}
              style={{
                width: '100%',
                padding: '14px',
                background: 'none',
                color: '#666',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AIAnalyzer;
